import { GEMINI_CONFIG } from '@/core/apiConfig';
import {
  normalizeParsedWorkout,
  validateWorkoutJSON,
} from '@/features/ai-generator/services/workoutJsonValidation';
import type { AIWorkoutResponse } from '@/types';
import type { WorkoutJobPollResponse, WorkoutJobStartResponse } from './workoutGenerationJob';
import { getTurnstileTokenForEnqueue } from '@/shared/security/turnstileEnqueue';

/** Id modelu używanego przez backend (Netlify `generate-workout`). */
export const GEMINI_MODEL_NAME = GEMINI_CONFIG.MODEL_NAME;

const GATEWAY_HTML_ERROR_PL =
  'Serwer pracuje nad Twoim zadaniem lub jest przeciążony (Error 504). Spróbuj ponownie za chwilę.';
const GATEWAY_HTML_ERROR_EN =
  'The server is still working on your task or is overloaded (Error 504). Please try again in a moment.';

function gatewayHtmlUserMessage(language: 'en' | 'pl'): string {
  return language === 'pl' ? GATEWAY_HTML_ERROR_PL : GATEWAY_HTML_ERROR_EN;
}

function responseLooksHtml(response: Response): boolean {
  const ct = response.headers.get('content-type') ?? '';
  return ct.toLowerCase().includes('text/html');
}

function throwHttpErrorFromBody(
  response: Response,
  errorText: string,
  language: 'en' | 'pl',
): never {
  if (responseLooksHtml(response)) {
    throw new Error(gatewayHtmlUserMessage(language));
  }
  let parsed: { error?: string } | null = null;
  try {
    parsed = JSON.parse(errorText) as { error?: string };
  } catch {
    /* body may be plain text */
  }
  throw new Error(JSON.stringify(parsed ?? { error: errorText.trim() || `HTTP_${response.status}` }));
}

/**
 * Enqueues async generation; returns `jobId` immediately (Netlify background worker + DB).
 */
export async function submitWorkoutGenerationJob(
  prompt: string,
  token?: string | null,
  guestGenerationCount?: number | null,
  language: 'en' | 'pl' = 'en',
): Promise<WorkoutJobStartResponse> {
  console.log('📡 [geminiService] submitWorkoutGenerationJob() START');
  console.log('   - Prompt length:', prompt.length);
  console.log('   - Auth token provided:', !!token);
  console.log('   - Guest generation count:', guestGenerationCount ?? 'n/a');

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const body: Record<string, unknown> = { prompt, language };
    if (guestGenerationCount != null && Number.isFinite(guestGenerationCount)) {
      body.guestGenerationCount = guestGenerationCount;
    }
    const turnstileToken = await getTurnstileTokenForEnqueue();
    if (turnstileToken) {
      body.turnstileToken = turnstileToken;
    }

    const startFetch = performance.now();
    const response = await fetch('/.netlify/functions/generate-workout', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const fetchTime = performance.now() - startFetch;
    console.log('📨 [geminiService] enqueue response in', fetchTime.toFixed(2), 'ms', 'status', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[geminiService] HTTP Error:', response.status, errorText.substring(0, 500));
      throwHttpErrorFromBody(response, errorText, language);
    }

    const raw: unknown = await response.json();
    if (!raw || typeof raw !== 'object') {
      throw new Error(JSON.stringify({ error: 'INVALID_JOB_RESPONSE' }));
    }
    const jobId = (raw as { jobId?: unknown }).jobId;
    const status = (raw as { status?: unknown }).status;
    if (typeof jobId !== 'string' || !jobId || status !== 'PROCESSING') {
      throw new Error(JSON.stringify({ error: 'INVALID_JOB_RESPONSE' }));
    }
    return { jobId, status: 'PROCESSING' };
  } catch (error) {
    console.error('❌ [geminiService] submitWorkoutGenerationJob error:', error);
    throw error;
  }
}

/**
 * Polls job status (fast Netlify handler; reads `workout_generation_jobs` via service role).
 */
export async function fetchWorkoutGenerationStatus(
  jobId: string,
  token?: string | null,
  language: 'en' | 'pl' = 'en',
  guestGenerationCount?: number | null,
): Promise<WorkoutJobPollResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const body: Record<string, unknown> = { jobId, language };
  if (guestGenerationCount != null && Number.isFinite(guestGenerationCount)) {
    body.guestGenerationCount = guestGenerationCount;
  }

  const response = await fetch('/.netlify/functions/generate-workout-status', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throwHttpErrorFromBody(response, errorText, language);
  }

  const raw: unknown = await response.json();
  if (!raw || typeof raw !== 'object' || !('status' in raw)) {
    throw new Error(JSON.stringify({ error: 'INVALID_STATUS_RESPONSE' }));
  }
  const st = (raw as { status: string }).status;
  if (st === 'PROCESSING') {
    return { status: 'PROCESSING' };
  }
  if (st === 'COMPLETED') {
    const result = (raw as { result?: unknown }).result;
    if (!validateWorkoutJSON(result)) {
      throw new Error(JSON.stringify({ error: 'AI_INVALID_RESPONSE' }));
    }
    const normalized = normalizeParsedWorkout(result);
    const usedModelId = (raw as { usedModelId?: unknown }).usedModelId;
    return {
      status: 'COMPLETED',
      result: normalized,
      usedModelId: typeof usedModelId === 'string' ? usedModelId : undefined,
    };
  }
  if (st === 'FAILED') {
    const error = (raw as { error?: unknown }).error;
    const errorCode = (raw as { errorCode?: unknown }).errorCode;
    const retryTime = (raw as { retryTime?: unknown }).retryTime;
    return {
      status: 'FAILED',
      error: typeof error === 'string' ? error : 'GENERATION_FAILED',
      errorCode: typeof errorCode === 'string' ? errorCode : undefined,
      retryTime: typeof retryTime === 'number' && Number.isFinite(retryTime) ? retryTime : undefined,
    };
  }
  throw new Error(JSON.stringify({ error: 'UNKNOWN_JOB_STATUS', status: st }));
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 15 * 60 * 1000;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Enqueues a job and polls until `COMPLETED` or `FAILED` (or timeout).
 * Used by the workout generator hook for a single-call UX while keeping HTTP responses short.
 */
export async function generateWorkoutData(
  prompt: string,
  token?: string | null,
  guestGenerationCount?: number | null,
  language: 'en' | 'pl' = 'en',
  onPollTick?: (tickIndex: number) => void,
): Promise<AIWorkoutResponse> {
  const { jobId } = await submitWorkoutGenerationJob(prompt, token, guestGenerationCount, language);
  const deadline = Date.now() + MAX_POLL_MS;
  let tick = 0;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    onPollTick?.(tick);
    tick += 1;

    const poll = await fetchWorkoutGenerationStatus(
      jobId,
      token,
      language,
      guestGenerationCount,
    );
    if (poll.status === 'COMPLETED') {
      return poll.result;
    }
    if (poll.status === 'FAILED') {
      const payload: Record<string, unknown> = {
        error: poll.errorCode ?? 'GENERATION_FAILED',
        message: poll.error,
      };
      if (poll.retryTime != null) {
        payload.retryTime = poll.retryTime;
      }
      throw new Error(JSON.stringify(payload));
    }
  }
  throw new Error(
    JSON.stringify({
      error: 'JOB_TIMEOUT',
      message: 'Generation took too long. Please try again.',
    }),
  );
}
